import { useId } from "react";
import { MdViewColumn, MdFilterList, MdClose } from "react-icons/md";
import { DropdownMenu, MenuCheckboxItem, MenuItem, MenuLabel, MenuSeparator } from "components/ui/DropdownMenu";
import Popover from "components/ui/Popover";
import Select from "components/ui/Select";

/* Controls ported from the tablecn pattern onto this codebase's own TanStack table
   shell rather than pulling in its Next.js/shadcn stack: a column-visibility menu, a
   per-column filter, and a page-size selector. Everything here is presentational and
   drives the table instance it is handed — no data fetching of its own.
   Styled as Kuest's DataTableViewOptions (dropdown of checkbox items), a Kuest popover,
   and Kuest's DataTablePagination "Rows per page" select. */

const CONTROL =
  "flex h-9 items-center gap-1.5 rounded-sm border border-border bg-background px-3 text-sm font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground data-[state=open]:bg-accent dark:bg-input/30 dark:hover:bg-input/50";

/** Human label for a column, falling back to its id when the header is a render fn. */
function columnLabel(column) {
  const header = column.columnDef.header;
  if (typeof header === "string") return header;
  if (typeof column.columnDef.meta?.label === "string") return column.columnDef.meta.label;
  return column.id.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());
}

export function ColumnVisibilityMenu({ table }) {
  const toggleable = table.getAllLeafColumns().filter((c) => c.id !== "__expand" && c.id !== "__expander" && c.getCanHide());
  if (!toggleable.length) return null;
  const hiddenCount = toggleable.filter((c) => !c.getIsVisible()).length;

  return (
    <DropdownMenu
      align="end"
      label="Toggle columns"
      className="w-56"
      trigger={({ props }) => (
        <button type="button" className={CONTROL} {...props}>
          <MdViewColumn className="text-base" />
          Columns
          {hiddenCount > 0 && (
            <span className="ml-0.5 rounded-md bg-primary px-1.5 text-[10px] font-semibold text-primary-foreground">{hiddenCount}</span>
          )}
        </button>
      )}
    >
      <MenuLabel>Toggle columns</MenuLabel>
      <MenuSeparator />
      {toggleable.map((column) => (
        <MenuCheckboxItem key={column.id} checked={column.getIsVisible()} onCheckedChange={(value) => column.toggleVisibility(Boolean(value))}>
          <span className="truncate">{columnLabel(column)}</span>
        </MenuCheckboxItem>
      ))}
      {hiddenCount > 0 && (
        <>
          <MenuSeparator />
          <MenuItem keepOpen onSelect={() => table.resetColumnVisibility()} className="text-muted-foreground">
            Reset
          </MenuItem>
        </>
      )}
    </DropdownMenu>
  );
}

/** Filters the rows already loaded on this page. Server-paginated tables only hold one
 *  page at a time, so the count is stated plainly rather than implying it searched
 *  everything — use the page's own search box for a server-wide query. */
export function TableFilter({ table, rowCount }) {
  const value = table.getState().globalFilter ?? "";
  const inputId = useId();

  return (
    <Popover
      align="end"
      label="Filter rows"
      trigger={({ props }) => (
        <button type="button" className={CONTROL} {...props}>
          <MdFilterList className="text-base" />
          Filter
          {value && <span className="ml-0.5 h-2 w-2 rounded-full bg-primary" aria-hidden="true" />}
        </button>
      )}
    >
      <label htmlFor={inputId} className="mb-1.5 block text-sm font-medium text-foreground">
        Filter these {rowCount} rows
      </label>
      <div className="relative">
        <input
          id={inputId}
          autoFocus
          value={value}
          onChange={(e) => table.setGlobalFilter(e.target.value)}
          placeholder="Type to narrow…"
          className="h-9 w-full rounded-md border border-border bg-transparent px-3 pr-8 text-sm text-foreground outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus:border-ring focus:ring-[3px] focus:ring-ring/50 dark:bg-input/30"
        />
        {value && (
          <button
            type="button"
            aria-label="Clear filter"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm text-muted-foreground hover:text-foreground"
            onClick={() => table.setGlobalFilter("")}
          >
            <MdClose />
          </button>
        )}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">Narrows the rows on this page only. Use the search box above to query every page.</p>
    </Popover>
  );
}

export function PageSizeSelect({ pageSize, onPageSizeChange, options = [10, 20, 50, 100] }) {
  if (typeof onPageSizeChange !== "function") return null;
  return (
    <div className="flex items-center gap-2 text-sm font-medium text-foreground">
      <span>Rows per page</span>
      <Select
        size="sm"
        ariaLabel="Rows per page"
        className="w-[4.5rem]"
        value={pageSize}
        onChange={(v) => onPageSizeChange(Number(v))}
        options={options.map((n) => ({ value: n, label: String(n) }))}
      />
    </div>
  );
}
